import {
  HeadContent,
  Scripts,
  createRootRoute,
  redirect,
  useRouterState,
} from '@tanstack/react-router'

import appCss from '../styles.css?url'
import { AppShell } from '../components/app-shell'
// NotFoundPage lives in its own tiny module (not pages.tsx) so the global 404
// the root renders does not pull the entire page module - and the full content
// catalog with every lesson body - into the eager root/shell bundle.
import { NotFoundPage } from '../components/not-found'
import { Providers } from '../components/providers'
import {
  BRAND_THEME_COLOR,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
  htmlLang,
} from '../lib/seo'
import {
  LocaleProvider,
  detectLocaleFromPath,
  resolveLegacyRedirect,
} from '../lib/i18n'
import type { Locale } from '../lib/i18n'
import { isDedicatedAuthPath } from '../lib/auth-route'
import { authHintBootstrap, billingHintBootstrap } from '../lib/paywall'
import { resolveClerkMount } from '../lib/auth/clerk-mount-runtime'
import type { ReactNode } from 'react'
// Montserrat is NOT imported from @fontsource here anymore. Those CSS files
// hardcode `font-display: swap` on every face, which flashes the fallback glyphs
// before swapping in Montserrat. We instead declare our own faces in styles.css
// with `font-display: block` (latin + latin-ext subsets), so the fallback is
// never painted - see the "Montserrat (self-declared, font-display: block)"
// block there. `font-display: block` only avoids a visible invisible-text gap if
// the font is ready at first paint, which is why we PRELOAD it (below).
//
// Since M6 Montserrat is the VARIABLE font: one latin woff2 (~38 KB) carries
// every weight that renders above the fold (400 body, 500 sub-headline, 600
// nav, 700 buttons, 800 H1), instead of five static files (~94 KB, five
// requests) competing with the LCP on mobile. Importing it as `?url` lets Vite
// hand us the final fingerprinted path, so we preload the EXACT file the
// browser will use. Only the `latin` subset is preloaded (it includes the
// German umlauts); latin-ext loads on demand via unicode-range.
import montserratLatinUrl from '@fontsource-variable/montserrat/files/montserrat-latin-wght-normal.woff2?url'

// Resource hints. Origins are derived from build-time env so they always track
// the deployment this build targets. Convex is preconnected on every page (its
// live-query socket serves public pages too: Pro lessons, newsletter, waitlist).
// Clerk's frontend API is preconnected ONLY on documents that mount Clerk (see
// `RootDocument`): anonymous visitors never talk to Clerk at all (M6).
function convexOrigin(): string | null {
  try {
    const convexUrl = import.meta.env.VITE_CONVEX_URL as string | undefined
    return convexUrl ? new URL(convexUrl).origin : null
  } catch {
    return null
  }
}

function clerkOrigin(): string | null {
  try {
    const key =
      (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined) ?? ''
    const host = atob(key.replace(/^pk_(test|live)_/u, '')).replace(/\$+$/u, '')
    return host ? `https://${host}` : null
  } catch {
    return null
  }
}

function deriveResourceHints(): Array<{
  rel: string
  href: string
  crossOrigin?: 'anonymous'
}> {
  const origin = convexOrigin()
  if (!origin) return []
  return [
    { rel: 'preconnect', href: origin, crossOrigin: 'anonymous' as const },
    { rel: 'dns-prefetch', href: origin },
  ]
}

const CLERK_ORIGIN = clerkOrigin()

const resourceHints = deriveResourceHints()

export const Route = createRootRoute({
  // Old German root URLs (/kurse, /wissen, /agb, ...) were the site's pre-i18n
  // home for content that now lives under /de. 301 them to their /de equivalent
  // so existing inbound links and indexed URLs never 404 and pass their SEO
  // weight on. The redirect table is derived from the shared route map.
  beforeLoad: ({ location }) => {
    const target = resolveLegacyRedirect(location.pathname)
    if (target && target !== location.pathname) {
      throw redirect({ to: target, statusCode: 301 })
    }
  },
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Learn Agentic Engineering: AI Agents, Claude Code & ROI',
      },
      {
        name: 'description',
        content:
          'Learn how to build real workflows and measurable ROI with AI agents, Claude Code and Codex. Start free with Courses 1-2, guides and tools.',
      },
      // Brand theme color from the generated asset pack (single source of truth
      // in src/lib/seo.ts), kept in sync with site.webmanifest + the Windows
      // tile color below.
      {
        name: 'theme-color',
        content: BRAND_THEME_COLOR,
      },
      {
        name: 'msapplication-TileColor',
        content: BRAND_THEME_COLOR,
      },
      {
        name: 'msapplication-config',
        content: '/browserconfig.xml',
      },
    ],
    links: [
      ...resourceHints,
      // Preload the ONE variable Montserrat file (every weight) so it starts
      // downloading in parallel with the CSS instead of after the browser
      // parses @font-face + matches a glyph. Our faces use `font-display:
      // block`, so text is laid out invisibly (in the metric-matched
      // fallback's geometry) until Montserrat arrives; preloading means it
      // arrives at/near first paint, so the heading (LCP) paints in its final
      // face immediately.
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: montserratLatinUrl,
        crossOrigin: 'anonymous' as const,
      },
      {
        rel: 'stylesheet',
        href: appCss,
      },
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '48x48',
        href: '/favicon-48x48.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '96x96',
        href: '/favicon-96x96.png',
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png',
      },
      { rel: 'manifest', href: '/site.webmanifest' },
      {
        rel: 'alternate',
        type: 'text/plain',
        href: '/llms.txt',
        title: 'llms.txt',
      },
    ],
  }),
  shellComponent: RootDocument,
  notFoundComponent: () => <NotFoundPage />,
})

// Runs before paint. Reads the `ae_theme` cookie (set by the theme toggle) and
// applies it to <html data-theme> so the very first frame already matches the
// user's choice.
//
// Light is the default. We deliberately do NOT fall back to the OS
// `prefers-color-scheme` here: every link in the app is a plain <a href> (full
// document load), so this script runs on every navigation. If it defaulted to
// the OS preference it would paint dark for a dark-OS visitor and then the
// React ThemeToggle (which defaults to light when no cookie exists) would flip
// it back to light on hydration - a dark→light flash on every page. Defaulting
// to light keeps SSR (`<html data-theme="light">`), this bootstrap, and the
// ThemeToggle all in agreement, so there is no flicker. Dark mode is opt-in via
// the toggle, which writes the `ae_theme=dark` cookie this script then honors
// pre-paint.
const themeBootstrap = `(() => {
  try {
    var m = document.cookie.match(/(?:^|; )ae_theme=([^;]+)/);
    var t = m ? decodeURIComponent(m[1]) : null;
    if (t !== 'dark' && t !== 'light') t = 'light';
    document.documentElement.dataset.theme = t;
  } catch (e) {}
})();`

// The homepage is a product-style landing page, so a hard refresh should always
// reopen at the hero instead of restoring a prior scroll position.
const homeScrollBootstrap = `(() => {
  try {
    if (location.pathname !== '/' || location.hash) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    scrollTo(0, 0);
    requestAnimationFrame(function () {
      scrollTo(0, 0);
    });
  } catch (e) {}
})();`

// Sitewide structured data. Rendered once per document so every page carries
// Organization + WebSite entities for rich results and the search box sitelink.
// The Organization entity is locale-independent and stringified once at module
// load; the WebSite entity carries `inLanguage` so it is built per active locale
// inside the document.
const organizationJsonLd = buildOrganizationJsonLd()

function buildSiteJsonLd(locale: Locale): string {
  return JSON.stringify([organizationJsonLd, buildWebSiteJsonLd(undefined, locale)])
}

function RootDocument({ children }: { children: ReactNode }) {
  // Locale is derived from the URL on both server and client (the `/de` prefix),
  // so SSR markup and hydration always agree. It drives `<html lang>` and the
  // shared LocaleProvider that every component reads from.
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const { locale } = detectLocaleFromPath(pathname)
  const isAuthPage = isDedicatedAuthPath(pathname)
  // Decided ONCE per document (server: path + cookie; browser: read back from
  // this attribute) so anonymous visitors never load Clerk. See clerk-mount.ts.
  const clerk = resolveClerkMount(pathname)

  return (
    // `suppressHydrationWarning` (this element's OWN attributes only, one
    // level deep): the pre-paint bootstraps below deliberately rewrite
    // `<html>` before React hydrates, from state the server cannot see
    // (`data-theme` from the theme cookie, `data-auth-hint` from Clerk's
    // cookie, `data-billing` from localStorage). Without it React reported a
    // hydration mismatch on every page after the first visit (M6). Children
    // are still fully checked.
    <html
      lang={htmlLang(locale)}
      data-theme="light"
      data-clerk={clerk ? '1' : undefined}
      suppressHydrationWarning
    >
      <head>
        <HeadContent />
        {clerk && CLERK_ORIGIN ? (
          <link rel="preconnect" href={CLERK_ORIGIN} crossOrigin="anonymous" />
        ) : null}
        <script dangerouslySetInnerHTML={{ __html: themeBootstrap }} />
        {/* Pre-paint signed-in hint: Pro lessons show a skeleton instead of
            flashing the paywall while Clerk + Convex confirm the session;
            the billing hint does the same in early access (BILLING_LIVE). */}
        <script
          dangerouslySetInnerHTML={{
            __html: authHintBootstrap + billingHintBootstrap,
          }}
        />
        <script dangerouslySetInnerHTML={{ __html: homeScrollBootstrap }} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: buildSiteJsonLd(locale) }}
        />
      </head>
      <body>
        <LocaleProvider locale={locale}>
          <Providers locale={locale} clerk={clerk}>
            {isAuthPage ? children : <AppShell>{children}</AppShell>}
          </Providers>
        </LocaleProvider>
        <Scripts />
      </body>
    </html>
  )
}
